import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../utils/api";

const AGE_GROUP_ORDER = ["6u", "7-8", "9-10", "11-12", "13-14"];
const AGE_GROUP_LABELS = {
  "6u": "6 & Under",
  "7-8": "7–8",
  "9-10": "9–10",
  "11-12": "11–12",
  "13-14": "13–14",
};

const TeamsView = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/league/teams`)
      .then((res) => {
        setTeams(res.data?.teams || []);
        setLoading(false);
      })
      .catch(() => {
        setError("Unable to load teams.");
        setLoading(false);
      });
  }, []);

  if (loading) return <div style={stateText}>Loading teams...</div>;
  if (error) return <div style={errorText}>{error}</div>;
  if (!teams.length) return <div style={stateText}>No teams found.</div>;

  return (
    <div style={page}>
      <h2 style={pageTitle}>Teams</h2>
      <div style={teamGrid}>
        {teams.map((team) => {
          const girls = team.swimmers.filter((s) => s.gender === "F");
          const boys = team.swimmers.filter((s) => s.gender === "M");

          // Group by age group
          const groupByAge = (swimmers) =>
            AGE_GROUP_ORDER.reduce((acc, ag) => {
              const inGroup = swimmers.filter((s) => s.age_group === ag);
              if (inGroup.length) acc[ag] = inGroup;
              return acc;
            }, {});

          const girlGroups = groupByAge(girls);
          const boyGroups = groupByAge(boys);

          return (
            <div key={team.coach} style={teamCard}>
              <h3 style={coachName}>{team.coach}</h3>

              {/* Girls */}
              {Object.keys(girlGroups).length > 0 && (
                <div style={genderSection}>
                  <span style={genderBadgeGirl}>Girls</span>
                  {AGE_GROUP_ORDER.filter((ag) => girlGroups[ag]).map((ag) => (
                    <div key={ag} style={ageGroup}>
                      <div style={ageGroupLabel}>{AGE_GROUP_LABELS[ag]}</div>
                      {girlGroups[ag].map((s) => (
                        <div key={s.name} style={swimmerRow}>
                          {s.name}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Boys */}
              {Object.keys(boyGroups).length > 0 && (
                <div style={genderSection}>
                  <span style={genderBadgeBoy}>Boys</span>
                  {AGE_GROUP_ORDER.filter((ag) => boyGroups[ag]).map((ag) => (
                    <div key={ag} style={ageGroup}>
                      <div style={ageGroupLabel}>{AGE_GROUP_LABELS[ag]}</div>
                      {boyGroups[ag].map((s) => (
                        <div key={s.name} style={swimmerRow}>
                          {s.name}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TeamsView;

// ── Styles ────────────────────────────────────────────────────────────────────

const page = { display: "flex", flexDirection: "column", gap: "0.75rem" };

const pageTitle = {
  margin: "0 0 0.25rem",
  fontSize: "1.15rem",
  fontWeight: 700,
  color: "#eaf3ff",
};

const stateText = { color: "#9fb7e3", padding: "0.5rem" };
const errorText = { color: "#ff93b0", padding: "0.5rem" };

const teamGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  gap: "0.75rem",
};

const teamCard = {
  border: "1px solid rgba(0,245,212,0.34)",
  borderRadius: "10px",
  background: "rgba(9,23,64,0.72)",
  padding: "0.75rem",
  display: "flex",
  flexDirection: "column",
  gap: "0.65rem",
  boxShadow: "inset 0 0 0 1px rgba(0,245,212,0.08)",
};

const coachName = {
  margin: 0,
  fontSize: "1rem",
  fontWeight: 800,
  color: "#eaf3ff",
  borderBottom: "1px solid rgba(0,245,212,0.2)",
  paddingBottom: "0.4rem",
};

const genderSection = {
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
};

const genderBadgeGirl = {
  display: "inline-block",
  fontSize: "0.68rem",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "#ffb3d1",
  background: "rgba(255,126,160,0.15)",
  border: "1px solid rgba(255,126,160,0.4)",
  borderRadius: "5px",
  padding: "0.1rem 0.5rem",
  marginBottom: "0.1rem",
};

const genderBadgeBoy = {
  display: "inline-block",
  fontSize: "0.68rem",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "#93c5fd",
  background: "rgba(96,165,250,0.15)",
  border: "1px solid rgba(96,165,250,0.4)",
  borderRadius: "5px",
  padding: "0.1rem 0.5rem",
  marginBottom: "0.1rem",
};

const ageGroup = {
  display: "flex",
  flexDirection: "column",
  gap: "0.15rem",
};

const ageGroupLabel = {
  fontSize: "0.66rem",
  fontWeight: 700,
  color: "#6884b7",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const swimmerRow = {
  fontSize: "0.84rem",
  fontWeight: 600,
  color: "#eaf3ff",
  padding: "0.22rem 0.4rem",
  borderRadius: "5px",
  background: "rgba(5,17,51,0.6)",
  border: "1px solid rgba(75,112,191,0.2)",
};
